import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getFeedback } from '../../services/adminService';

const PAGE_LIMIT = 20;

const EASE_LABELS = {
  muy_dificil: 'Muy difícil',
  dificil: 'Difícil',
  neutral: 'Neutral',
  facil: 'Fácil',
  muy_facil: 'Muy fácil',
};

const TASK_COMPLETED_CONFIG = {
  si_facilmente:   { label: 'Sí, fácilmente',        color: '#28a745', bg: '#eaf7ed' },
  si_con_esfuerzo: { label: 'Sí, con esfuerzo',       color: '#f0a500', bg: '#fef9e7' },
  no_desisti:      { label: 'No, desistió',           color: '#dc3545', bg: '#fde8ea' },
  no_aplica:       { label: 'No aplica',              color: '#888',    bg: '#f0f0f0' },
};

const SOURCE_CONFIG = {
  web:   { label: 'Web',   color: '#2ca9e1', bg: '#e8f6fd' },
  kiosk: { label: 'Kiosco', color: '#7c3aed', bg: '#f5f0fe' },
};

const SOURCE_OPTIONS = [
  { value: '',      label: 'Todas las fuentes' },
  { value: 'web',   label: 'Web' },
  { value: 'kiosk', label: 'Kiosco' },
];

const Badge = ({ cfg, text }) => (
  <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: '12px', fontSize: '0.78rem', fontWeight: '600', color: cfg.color, background: cfg.bg, whiteSpace: 'nowrap' }}>
    {text || cfg.label}
  </span>
);

const formatDate = (iso) =>
  new Date(iso).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' });

const selectStyle = {
  padding: '0.5rem 0.75rem',
  border: '1px solid #ddd',
  borderRadius: '8px',
  fontSize: '0.9rem',
  outline: 'none',
  cursor: 'pointer',
  background: 'white',
};

const truncateStyle = { maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };

const NOTE_FIELDS = [
  { key: 'notesIntro',           label: 'Introducción' },
  { key: 'notesFirstImpression', label: 'Primera impresión' },
  { key: 'notesTask1',           label: 'Tarea 1' },
  { key: 'notesTask2',           label: 'Tarea 2' },
  { key: 'notesTask3',           label: 'Tarea 3' },
  { key: 'notesClosing',         label: 'Cierre' },
  { key: 'notesGeneral',         label: 'Observaciones generales' },
];

// Facilitator notes from the "Guion de Prueba de Usabilidad Moderada" step
// (migrations/066) — combined into one tooltip rather than 7 extra columns.
const facilitatorNotesSummary = (r) => {
  const filled = NOTE_FIELDS.filter(({ key }) => r[key]?.trim());
  if (filled.length === 0) return null;
  return {
    count: filled.length,
    tooltip: filled.map(({ key, label }) => `${label}: ${r[key].trim()}`).join('\n\n'),
  };
};

const AdminFeedbackPage = () => {
  const { session } = useAuth();
  const [responses, setResponses] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [sourceFilter, setSourceFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => { setPage(1); }, [sourceFilter]);

  useEffect(() => {
    if (!session?.access_token) { setLoading(false); setError('No hay sesión activa.'); return; }
    let cancelled = false;
    setLoading(true);

    const params = { page, limit: PAGE_LIMIT };
    if (sourceFilter) params.source = sourceFilter;

    getFeedback(session.access_token, params)
      .then((data) => {
        if (cancelled) return;
        setResponses(data.responses || []);
        setTotal(data.total || 0);
        setTotalPages(data.totalPages || 1);
        setError(null);
      })
      .catch((err) => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [session?.access_token, page, sourceFilter]);

  return (
    <div className="container" style={{ padding: '2rem 0 4rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
        <Link to="/admin" style={{ color: '#888', textDecoration: 'none', fontSize: '0.9rem' }}>← Panel</Link>
        <h1 style={{ fontFamily: "'Gambado Sans', sans-serif", fontSize: '1.6rem', color: '#333', margin: 0 }}>
          Feedback de usabilidad
        </h1>
        <span style={{ color: '#888', fontSize: '0.9rem', marginLeft: 'auto' }}>
          {total} respuesta{total !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', margin: '1.5rem 0' }}>
        <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)} style={selectStyle}>
          {SOURCE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      {error && <p style={{ color: '#dc3545', marginBottom: '1rem' }}>{error}</p>}

      <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
            <thead>
              <tr style={{ background: '#fafafa' }}>
                {['Fecha', 'Facilidad', 'Tarea', 'Completó', 'Diseño', 'NPS', 'Fuente', 'Contacto', 'Notas del facilitador'].map((h) => (
                  <th key={h} style={{ padding: '0.75rem 1rem', textAlign: 'left', color: '#666', fontWeight: '600', whiteSpace: 'nowrap', borderBottom: '1px solid #f0f0f0' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} style={{ padding: '2.5rem', textAlign: 'center', color: '#888' }}>Cargando...</td></tr>
              ) : responses.length === 0 ? (
                <tr><td colSpan={9} style={{ padding: '2.5rem', textAlign: 'center', color: '#888' }}>No hay respuestas todavía.</td></tr>
              ) : responses.map((r) => {
                const taskCfg = TASK_COMPLETED_CONFIG[r.taskCompleted] || { label: r.taskCompleted, color: '#666', bg: '#f0f0f0' };
                const sourceCfg = SOURCE_CONFIG[r.source] || { label: r.source, color: '#666', bg: '#f0f0f0' };
                const notes = facilitatorNotesSummary(r);
                return (
                  <tr key={r.id} style={{ borderBottom: '1px solid #f8f8f8' }}>
                    <td style={{ padding: '0.7rem 1rem', whiteSpace: 'nowrap', color: '#555' }}>{formatDate(r.createdAt)}</td>
                    <td style={{ padding: '0.7rem 1rem', color: '#333' }}>{EASE_LABELS[r.easeOfFinding] || r.easeOfFinding}</td>
                    <td style={{ padding: '0.7rem 1rem', color: '#333', ...truncateStyle }} title={r.taskDescription}>{r.taskDescription}</td>
                    <td style={{ padding: '0.7rem 1rem' }}>
                      <Badge cfg={taskCfg} />
                      {r.obstacle && (
                        <div style={{ fontSize: '0.75rem', color: '#888', marginTop: '0.25rem', ...truncateStyle }} title={r.obstacle}>{r.obstacle}</div>
                      )}
                    </td>
                    <td style={{ padding: '0.7rem 1rem', color: '#333', fontWeight: '600' }}>{r.designRating}/5</td>
                    <td style={{ padding: '0.7rem 1rem', color: '#333' }}>{r.npsScore ?? '—'}</td>
                    <td style={{ padding: '0.7rem 1rem' }}><Badge cfg={sourceCfg} /></td>
                    <td style={{ padding: '0.7rem 1rem', color: '#555' }}>{r.wantsCall ? (r.contactEmail || 'Sí') : '—'}</td>
                    <td style={{ padding: '0.7rem 1rem' }}>
                      {notes ? (
                        <span title={notes.tooltip} style={{ display: 'inline-block', padding: '2px 10px', borderRadius: '12px', fontSize: '0.78rem', fontWeight: '600', color: '#0d9488', background: '#e6fbf8', cursor: 'help' }}>
                          📝 {notes.count} sección{notes.count !== 1 ? 'es' : ''}
                        </span>
                      ) : (
                        <span style={{ color: '#bbb' }}>—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '1.5rem', justifyContent: 'center' }}>
          <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
            style={{ padding: '0.5rem 1rem', border: '1px solid #ddd', borderRadius: '8px', background: page === 1 ? '#f8f9fa' : 'white', color: page === 1 ? '#aaa' : '#333', cursor: page === 1 ? 'default' : 'pointer', fontSize: '0.9rem' }}>
            ← Anterior
          </button>
          <span style={{ color: '#666', fontSize: '0.9rem' }}>Página {page} de {totalPages}</span>
          <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}
            style={{ padding: '0.5rem 1rem', border: '1px solid #ddd', borderRadius: '8px', background: page === totalPages ? '#f8f9fa' : 'white', color: page === totalPages ? '#aaa' : '#333', cursor: page === totalPages ? 'default' : 'pointer', fontSize: '0.9rem' }}>
            Siguiente →
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminFeedbackPage;
